import { DateTime } from "luxon";
import { actualMatchesScheduled, getShiftTotalHours, type ResolvedActualDay } from "../components/StaffTable/utils";
import type { Department } from "../contexts/AuthProvider";
import { formatPersonFullName } from "./formatters";
import {
	AbsenceType,
	type BaseNurseShift,
	CALL_IN_DEFAULT_REASON,
	type CalledOffShift,
	type NotScheduledEmployee,
	type Nurse,
	type NurseInShift,
	type NurseRef,
	type PtoRef,
	type PunchSummary,
	type PunchSummaryCategory,
	type ShiftDeltaEmployee,
	type SpecificPreference,
} from "./models";
import { isNursePunchExpected, isNurseSalariedForShift, isShiftExcludedFromPpd } from "./wageUtils";
import { isAgencyNurse } from "./warningDialogs/utils";

// PTO masking is applied to `noPunch` so nurses on approved PTO are not flagged.

// Call-in bucket mirrors the Disciplinary Report — only `AbsenceType.CALL_IN` counts.
// NCNS, management-initiated removals, and INH-3258 custom reasons are excluded.

export type ComputePunchSummaryInput = {
	shifts: BaseNurseShift[];
	resolvedActual: ResolvedActualDay;
	getNurseById: (id: number) => Nurse | undefined;
	department: Pick<Department, "department_id" | "dayStart" | "breakRulesConfig" | "shift_delta_threshold_hours">;
	pto: SpecificPreference[];
	jobManagementEnabled: boolean;
	/** The department's name-order flag, for the names this builds from the roster (PTO, call-ins). */
	lastNameFirst: boolean;
	costlyShiftsForDay?: NurseInShift[];
	shiftHoursById?: Map<string, { totalHours: number; dailyOtHours: number; weeklyOtHours: number }>;
	callOffsForDay?: CalledOffShift[];
	/** Injectable clock for the pending-punch-out grace. Defaults to the current instant. */
	now?: DateTime;
};

// A worked nurse on a PPD-excluded job is salaried/admin staff that isn't scheduled; pulling them
// into their own bucket keeps them out of every actual-derived bucket (notScheduled, staff,
// shiftDelta, ot, agency, noPunch, missingPunch) so they don't inflate those counts.
const isExcludedFromPpd = (shift: NurseInShift, jobManagementEnabled: boolean, getNurseById: (id: number) => Nurse | undefined): boolean =>
	isShiftExcludedFromPpd(jobManagementEnabled, shift.excludeFromPpd, jobManagementEnabled ? undefined : getNurseById(shift.nurseId)?.excludeFromPpd);

const toNurseRef = (shift: BaseNurseShift): NurseRef => ({ nurseId: shift.nurseId, name: shift.name });

const ptoEntriesFromPreferences = (pto: SpecificPreference[], getNurseById: (id: number) => Nurse | undefined, lastNameFirst: boolean): PtoRef[] => {
	const out: PtoRef[] = [];
	for (const pref of pto) {
		const nurse = getNurseById(pref.nurse_id);
		if (!nurse) continue;
		out.push({
			nurseId: nurse.id,
			name: formatPersonFullName(nurse, lastNameFirst),
			userId: nurse.userId,
			preferenceId: pref.id,
			hours: pref.hours,
			reason: pref.reason as string,
		});
	}
	return out;
};

const sumHoursByNurseId = (
	shifts: BaseNurseShift[],
	department: Pick<Department, "department_id" | "breakRulesConfig">,
	getNurseById: (id: number) => Nurse | undefined,
): Map<number, number> => {
	const totals = new Map<number, number>();
	for (const shift of shifts) {
		const hours = getShiftTotalHours(
			shift.startTime,
			shift.endTime,
			shift.role,
			shift.type,
			department,
			undefined,
			undefined,
			getNurseById(shift.nurseId)?.union,
		);
		totals.set(shift.nurseId, (totals.get(shift.nurseId) ?? 0) + hours);
	}
	return totals;
};

const dedupRefs = <T extends NurseRef>(refs: T[]): T[] => {
	const seen = new Set<number>();
	const out: T[] = [];
	for (const r of refs) {
		if (seen.has(r.nurseId)) continue;
		seen.add(r.nurseId);
		out.push(r);
	}
	return out;
};

const round = (n: number): number => Math.round(n * 100) / 100;

export const PUNCH_SUMMARY_CATEGORY_DOT_HEX: Record<PunchSummaryCategory, string> = {
	noPunch: "#ef4444", // red-500
	missingPunch: "#f59e0b", // amber-500
	shiftDelta: "#8b5cf6", // violet-500
	ot: "#f97316", // orange-500
	pto: "#60a5fa", // blue-400
	callIn: "#fb7185", // rose-400
	agency: "#6366f1", // indigo-500
	notScheduled: "#9ca3af", // gray-400 (mockup fallback)
	excludedFromPpd: "#14b8a6", // teal-500
};

/**
 * A punch-out we have not received yet is not a clerical miss. Overnight staff are still mid-shift,
 * or their clock data simply hasn't synced, so flagging at the rostered end produces a wall of false
 * gaps a manager has to mentally discount every morning. Wait this long past the scheduled end
 * before calling it a missing punch. DAT-803.
 */
export const PENDING_PUNCH_OUT_GRACE_HOURS = 10;

/**
 * Is this half punch merely *waiting* for its punch-out, rather than missing one?
 *
 * Measured from the SCHEDULED end, never the actual: a missing-punch-out row is zero-length because
 * the syncher anchors both ends on the one real punch, so the actual's `endTime` is the punch-IN.
 *
 * Only a pending punch-OUT waits. A missing punch-IN means the OUT already arrived, so the pair has
 * synced and the gap is a real clerical miss with nothing left to wait for.
 */
const isPunchOutStillPending = (actual: NurseInShift, scheduledForNurse: BaseNurseShift[], now: DateTime): boolean => {
	if (!actual.missingPunchOut || actual.missingPunchIn) return false;
	// No rostered shift means no "when was it over" to measure from, so flag it straight away.
	const scheduled = scheduledForNurse.find((shift) => actualMatchesScheduled(shift, actual));
	return !!scheduled && now < scheduled.endTime.plus({ hours: PENDING_PUNCH_OUT_GRACE_HOURS });
};

const groupShiftsByNurseId = (shifts: BaseNurseShift[]): Map<number, BaseNurseShift[]> => {
	const byNurseId = new Map<number, BaseNurseShift[]>();
	for (const shift of shifts) {
		const existing = byNurseId.get(shift.nurseId);
		if (existing) existing.push(shift);
		else byNurseId.set(shift.nurseId, [shift]);
	}
	return byNurseId;
};

export const computePunchSummary = ({
	shifts,
	resolvedActual,
	getNurseById,
	department,
	pto,
	jobManagementEnabled,
	lastNameFirst,
	costlyShiftsForDay,
	shiftHoursById,
	callOffsForDay,
	// DEFAULT: injectable clock. Production has exactly one caller (`WeekViewProvider`) and no clock
	// of its own, so the parameter exists purely so tests can pin `now` without fake timers.
	now = DateTime.now(),
}: ComputePunchSummaryInput): PunchSummary => {
	// `actualShifts` already has scheduled shifts swapped in for staff who don't clock in, which is
	// what keeps them out of both `noPunch` and `missingPunch`.
	const { actualShifts, missingFromScheduled } = resolvedActual;
	const excludedActual: NurseInShift[] = [];
	const remainingActual: NurseInShift[] = [];
	for (const shift of actualShifts) {
		(isExcludedFromPpd(shift, jobManagementEnabled, getNurseById) ? excludedActual : remainingActual).push(shift);
	}
	const excludedNurseIds = new Set<number>(excludedActual.map((s) => s.nurseId));

	const invalidNurses: BaseNurseShift[] = [
		...remainingActual.filter((actual) => actual.missingPunchIn || actual.missingPunchOut),
		...missingFromScheduled.filter((shift) => !excludedNurseIds.has(shift.nurseId)),
	];
	const ptoNurseIds = new Set<number>(pto.map((p) => p.nurse_id));
	const ptoRefs = ptoEntriesFromPreferences(pto, getNurseById, lastNameFirst);

	const scheduledNurseIds = new Set(shifts.map((s) => s.nurseId));

	const notScheduled = remainingActual.filter((nurse) => !scheduledNurseIds.has(nurse.nurseId));
	const notScheduledIds = new Set(notScheduled.map((n) => n.nurseId));

	const scheduledByNurseId = groupShiftsByNurseId(shifts);
	const isPendingPunchOut = (nurse: NurseInShift): boolean => isPunchOutStillPending(nurse, scheduledByNurseId.get(nurse.nurseId) ?? [], now);

	// Staff who don't clock in are never flagged for a punch gap. `resolveActualDayShifts` already
	// swaps in their scheduled times, but a stray punch with no stand-in can still land here.
	const missingPunch = remainingActual.filter(
		(nurse) =>
			!notScheduledIds.has(nurse.nurseId) &&
			scheduledNurseIds.has(nurse.nurseId) &&
			isNursePunchExpected(getNurseById, nurse.nurseId, nurse.startTime) &&
			!isPendingPunchOut(nurse) &&
			((nurse.missingPunchIn && !nurse.missingPunchOut) || (!nurse.missingPunchIn && nurse.missingPunchOut)),
	);
	const missingPunchIds = new Set(missingPunch.map((n) => n.nurseId));

	// Graced nurses drop out of `missingPunchIds`, which is also the guard keeping a half punch out of
	// `noPunch` below. Track them separately so waiting on a punch-out can never read as "never punched".
	// Re-running the predicate rather than reusing this Set in the filter above is deliberate: the
	// filter must stay per-SHIFT, so a nurse with one graced shift and one genuinely overdue shift is
	// still flagged for the overdue one. This Set is per-nurse because the `noPunch` guard is.
	const pendingPunchOutIds = new Set(remainingActual.filter(isPendingPunchOut).map((n) => n.nurseId));

	// Restrict noPunch to scheduled nurses. Otherwise actual-only nurses with a partial punch
	// (e.g. punched-in but never punched-out → zero-duration actual) sneak in, because the
	// self-overlap check below relies on `scheduledDurationMs > 0` which fails for them.
	// Defense in depth: a nurse already classified as missing-punch must never duplicate here.
	const noPunchCandidates = invalidNurses.filter(
		(nurse) =>
			scheduledNurseIds.has(nurse.nurseId) &&
			!ptoNurseIds.has(nurse.nurseId) &&
			!missingPunchIds.has(nurse.nurseId) &&
			!pendingPunchOutIds.has(nurse.nurseId) &&
			!remainingActual.some((actual) => actual.nurseId === nurse.nurseId && actualMatchesScheduled(nurse, actual)),
	);
	// Agency staff (perDiem/travel) who never punched read as an agency staffing gap, not an
	// in-house attendance gap, so they're routed into the Agency bucket instead of No Punch.
	const noPunch: BaseNurseShift[] = [];
	const noPunchAgencyCandidates: BaseNurseShift[] = [];
	for (const nurse of noPunchCandidates) {
		const employmentType = getNurseById(nurse.nurseId)?.employmentType ?? nurse.type;
		(isAgencyNurse(employmentType) ? noPunchAgencyCandidates : noPunch).push(nurse);
	}

	const regularStaff = remainingActual.filter((nurse) => !notScheduledIds.has(nurse.nurseId) && !missingPunchIds.has(nurse.nurseId));

	const scheduledHoursByNurseId = sumHoursByNurseId(shifts, department, getNurseById);
	const actualHoursByNurseId = sumHoursByNurseId(regularStaff, department, getNurseById);

	const regularStaffById = new Map<number, NurseInShift>(regularStaff.map((s) => [s.nurseId, s]));
	const shiftDeltaEmployees: ShiftDeltaEmployee[] = [];
	const shiftDeltaNurseIds = new Set<number>();
	let totalDeltaHours = 0;
	for (const [nurseId, actualHours] of actualHoursByNurseId) {
		// Skip salaried staff — their extra hours are part of their salary, so shift delta is
		// meaningless for them. Wage type comes from the base wage for the shift date.
		if (isNurseSalariedForShift(getNurseById, nurseId, regularStaffById.get(nurseId)?.startTime)) continue;
		const scheduledHours = scheduledHoursByNurseId.get(nurseId) ?? 0;
		const delta = actualHours - scheduledHours;
		if (delta > department.shift_delta_threshold_hours) {
			shiftDeltaNurseIds.add(nurseId);
			totalDeltaHours += delta;
			const ref = regularStaffById.get(nurseId);
			if (ref) {
				shiftDeltaEmployees.push({
					nurseId,
					name: ref.name,
					scheduledHours: round(scheduledHours),
					actualHours: round(actualHours),
					diffHours: round(delta),
				});
			}
		}
	}

	const remainingStaff = regularStaff.filter((nurse) => !shiftDeltaNurseIds.has(nurse.nurseId));

	const actualHoursForNotScheduled = sumHoursByNurseId(notScheduled, department, getNurseById);
	const notScheduledEmployees: NotScheduledEmployee[] = [];
	let totalNotScheduledActual = 0;
	for (const nurse of dedupRefs(notScheduled.map(toNurseRef))) {
		const hours = actualHoursForNotScheduled.get(nurse.nurseId) ?? 0;
		totalNotScheduledActual += hours;
		notScheduledEmployees.push({ ...nurse, actualHours: round(hours) });
	}

	const actualHoursForExcluded = sumHoursByNurseId(excludedActual, department, getNurseById);
	const excludedFromPpdEmployees: NotScheduledEmployee[] = [];
	let totalExcludedActual = 0;
	for (const nurse of dedupRefs(excludedActual.map(toNurseRef))) {
		const hours = actualHoursForExcluded.get(nurse.nurseId) ?? 0;
		totalExcludedActual += hours;
		excludedFromPpdEmployees.push({ ...nurse, actualHours: round(hours) });
	}

	const otByNurse = new Map<number, { name: string; hours: number }>();
	for (const shift of costlyShiftsForDay ?? []) {
		if (isExcludedFromPpd(shift, jobManagementEnabled, getNurseById)) continue;
		if (isAgencyNurse(shift.type)) continue;
		const hours =
			shiftHoursById?.get(shift.id)?.weeklyOtHours ??
			getShiftTotalHours(shift.startTime, shift.endTime, shift.role, shift.type, department, undefined, undefined, getNurseById(shift.nurseId)?.union);
		const existing = otByNurse.get(shift.nurseId);
		otByNurse.set(shift.nurseId, { name: shift.name, hours: (existing?.hours ?? 0) + hours });
	}
	const otEmployees = Array.from(otByNurse.entries())
		.filter(([, v]) => v.hours > 0)
		.map(([nurseId, v]) => ({ nurseId, name: v.name, otHours: round(v.hours) }));
	const totalOtHours = otEmployees.reduce((acc, e) => acc + e.otHours, 0);

	const agencyByNurse = new Map<number, { name: string; hours: number; agencyName?: string }>();
	for (const shift of remainingActual) {
		const nurse = getNurseById(shift.nurseId);
		const employmentType = nurse?.employmentType ?? shift.type;
		if (!isAgencyNurse(employmentType)) continue;
		const hours = getShiftTotalHours(shift.startTime, shift.endTime, shift.role, shift.type, department, undefined, undefined, nurse?.union);
		const existing = agencyByNurse.get(shift.nurseId);
		agencyByNurse.set(shift.nurseId, {
			name: shift.name,
			hours: (existing?.hours ?? 0) + hours,
			agencyName: nurse?.agencyName,
		});
	}
	const agencyEmployees = Array.from(agencyByNurse.entries()).map(([nurseId, v]) => ({
		nurseId,
		name: v.name,
		agencyHours: round(v.hours),
		missingPunch: false,
		agencyName: v.agencyName,
	}));
	// No-punch agency nurses contribute 0 hours, so appending them before summing doesn't affect the total.
	// Defense in depth: a nurse who already has a worked/punched agency shift that day (in `agencyByNurse`)
	// must never duplicate here — e.g. a double-shift agency nurse with one punched shift and one
	// unpunched shift should show once, as worked, not also as a separate missing-punch entry.
	const noPunchAgencyEmployees = dedupRefs(noPunchAgencyCandidates.map(toNurseRef))
		.filter((ref) => !agencyByNurse.has(ref.nurseId))
		.map((ref) => ({
			...ref,
			agencyHours: 0,
			missingPunch: true,
			agencyName: getNurseById(ref.nurseId)?.agencyName,
		}));
	const allAgencyEmployees = [...agencyEmployees, ...noPunchAgencyEmployees];
	const totalAgencyHours = allAgencyEmployees.reduce((acc, e) => acc + e.agencyHours, 0);

	// Strict match to the Disciplinary Report's call-in definition: `message == AbsenceType.CALL_IN`.
	const callInByNurse = new Map<number, { name: string; reason: string }>();
	for (const co of callOffsForDay ?? []) {
		if (co.message !== AbsenceType.CALL_IN) continue;
		if (callInByNurse.has(co.nurseId)) continue;
		const nurse = getNurseById(co.nurseId);
		const name = nurse ? formatPersonFullName(nurse, lastNameFirst) : "";
		callInByNurse.set(co.nurseId, { name, reason: co.message || CALL_IN_DEFAULT_REASON });
	}
	const callInEmployees = Array.from(callInByNurse.entries()).map(([nurseId, v]) => ({
		nurseId,
		name: v.name,
		reason: v.reason,
	}));

	return {
		noPunch: dedupRefs(noPunch.map(toNurseRef)),
		notScheduled: {
			totalScheduledHours: 0,
			totalActualHours: round(totalNotScheduledActual),
			totalDiffHours: round(totalNotScheduledActual),
			employees: notScheduledEmployees,
		},
		excludedFromPpd: {
			totalActualHours: round(totalExcludedActual),
			employees: excludedFromPpdEmployees,
		},
		missingPunch: dedupRefs(missingPunch.map(toNurseRef)),
		shiftDelta: {
			totalDeltaHours: round(totalDeltaHours),
			employees: shiftDeltaEmployees,
		},
		pto: ptoRefs,
		ot: { totalOtHours: round(totalOtHours), employees: otEmployees },
		agency: { totalAgencyHours: round(totalAgencyHours), employees: allAgencyEmployees },
		callIn: { totalCount: callInEmployees.length, employees: callInEmployees },
		staff: dedupRefs(remainingStaff.map(toNurseRef)),
	};
};

export type { SpecificPreference };
